<script lang="ts">
import type { StreamCheck } from '@codot/contracts';
import { streamApi } from '../../api/stream.api';

/** One server check per link while the CMS is open (the form's "Check link" and the preview share it). */
const checks = new Map<string, Promise<StreamCheck>>();
export function checkStreamLink(url: string, fresh = false): Promise<StreamCheck> {
  if (fresh) checks.delete(url);
  let p = checks.get(url);
  if (!p) {
    p = streamApi.check(url);
    checks.set(url, p);
    p.catch(() => checks.delete(url));
  }
  return p;
}
/** Is this a link the app can save (https, no spaces)? */
export const streamLinkLooksRight = (url: string): boolean => /^https:\/\/[^\s/]+\.[^\s]+$/i.test(url.trim());
</script>

<script setup lang="ts">
/**
 * Streaming video app preview, played like the web player (player-web render/stream.ts): the browser's own player
 * for HLS / DASH where it has one, else hls.js / dash.js. The link is checked by the server first; until it plays
 * (or when it can't) the app's fallback shows. Thumbnails (`live` off) never connect.
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { streamKind, type StreamWidget } from '@codot/contracts';

const props = withDefaults(defineProps<{ config: StreamWidget; live?: boolean }>(), { live: true });
const { t } = useI18n();
const video = ref<HTMLVideoElement | null>(null);
const check = ref<StreamCheck | null>(null);
const playing = ref(false);
const failed = ref(false);
let destroy: (() => void) | null = null;
let timer = 0;
let gen = 0;

const teardown = (): void => {
  gen++;
  destroy?.();
  destroy = null;
  playing.value = false;
  const v = video.value;
  if (v) {
    v.removeAttribute('src');
    v.load();
  }
};

async function attach(url: string): Promise<void> {
  const mine = gen;
  const v = video.value;
  if (!v) return;
  const kind = streamKind(url);
  try {
    // hls.js wherever MSE exists: Chromium's native HLS often never starts. Native only without MSE (Safari).
    const nativeHls = v.canPlayType('application/vnd.apple.mpegurl') !== '';
    if (kind === 'hls' && ('MediaSource' in window || 'ManagedMediaSource' in window || !nativeHls)) {
      const { default: Hls } = await import('hls.js');
      if (mine !== gen) return;
      if (!Hls.isSupported()) {
        if (!nativeHls) throw new Error('no HLS');
        v.src = url;
        void v.play().catch(() => undefined);
        return;
      }
      const hls = new Hls();
      hls.on(Hls.Events.ERROR, (_e, d) => d.fatal && mine === gen && (failed.value = true));
      hls.loadSource(url);
      hls.attachMedia(v);
      destroy = () => hls.destroy();
    } else if (kind === 'dash' && !v.canPlayType('application/dash+xml')) {
      const dashjs = await import('dashjs');
      if (mine !== gen) return;
      const p = dashjs.MediaPlayer().create();
      p.on('error', () => mine === gen && (failed.value = true));
      p.initialize(v, url, true);
      destroy = () => p.destroy();
    } else {
      v.src = url;
    }
    void v.play().catch(() => undefined);
  } catch {
    failed.value = true;
  }
}

const url = computed(() => props.config.url.trim());
watch(
  () => [url.value, props.live] as const,
  ([u, live]) => {
    clearTimeout(timer);
    teardown();
    check.value = null;
    failed.value = false;
    if (!live || !streamLinkLooksRight(u)) return;
    const mine = gen;
    timer = window.setTimeout(() => {
      void checkStreamLink(u).then(
        (c) => {
          if (mine !== gen) return;
          check.value = c;
          if (c.verdict === 'ok') void attach(u);
        },
        () => mine === gen && (failed.value = true),
      );
    }, 500);
  },
  { immediate: true, flush: 'post' },
);
onBeforeUnmount(() => {
  clearTimeout(timer);
  teardown();
});

const ar = computed(() => props.config.language === 'ar');
/** What the cover says: the app's own fallback text, or why the preview isn't playing. */
const note = computed(() => {
  const c = props.config;
  if (!url.value) return t('appsCat.stEnterLink');
  if (!streamLinkLooksRight(url.value)) return t('appsCat.stBadLink');
  if (check.value && check.value.verdict !== 'ok') return t(`appsCat.stVerdict_${check.value.verdict}`);
  if (c.fallbackText) return c.fallbackText;
  if (c.posterUrl && !failed.value) return '';
  return failed.value ? (ar.value ? 'البث المباشر غير متاح' : 'The live stream is unavailable') : ar.value ? 'جارٍ الاتصال بالبث المباشر…' : 'Connecting to the live stream…';
});
</script>

<template>
  <div class="relative h-full w-full overflow-hidden" :style="{ background: config.background, color: config.foreground }" data-testid="stream-preview">
    <video
      v-if="live"
      ref="video"
      class="absolute inset-0 h-full w-full"
      :style="{ objectFit: config.fit, visibility: playing ? 'visible' : 'hidden' }"
      :muted="config.muted"
      autoplay
      playsinline
      @playing="(playing = true), (failed = false)"
      @error="failed = true"
    />
    <div
      v-if="!playing"
      class="absolute inset-0 flex items-center justify-center bg-no-repeat text-center"
      :style="{ background: config.background, backgroundImage: config.posterUrl ? `url('${config.posterUrl.replace(/'/g, '%27')}')` : undefined, backgroundSize: config.fit, backgroundPosition: 'center', padding: '4cqmin' }"
      :dir="ar ? 'rtl' : 'ltr'"
      role="status"
    >
      <span v-if="note" class="max-w-[90%] font-semibold leading-snug" :style="{ fontSize: 'min(6cqh, 4cqw)', textShadow: '0 1px 3px rgba(0,0,0,.6)' }">
        <span v-if="!live" aria-hidden="true">📡 </span>{{ note }}
      </span>
    </div>
  </div>
</template>
